// How long a technical job has been waiting, and how long a finished one took.
//
// Ported from the Flutter client's presentation/format_helpers.dart so both
// clients age the same job identically - two people reading the same TW number
// on a phone and on the web must not see different numbers.

import { isFinalized as isFinalizedStatus } from "./technicalStatusMaps";

/**
 * Time since `isoDateTime`, Flutter's exact shape:
 *   under 24h -> decimal hours, "2.11hrs" (a fresh complaint reading "0d 2hrs"
 *   said nothing), 24h and over -> "1d 13hrs".
 * `isLong` flips at the same 24-hour mark and drives the red/green pill.
 */
export const formatElapsedSince = (isoDateTime) => {
  if (!isoDateTime) return null;
  const from = new Date(isoDateTime);
  if (Number.isNaN(from.getTime())) return null;

  const ms = Date.now() - from.getTime();
  if (ms < 0) return { label: "0.00hrs", isLong: false, ms: 0 };

  const minutes = Math.floor(ms / 60000);
  if (minutes < 24 * 60) return { label: `${(minutes / 60).toFixed(2)}hrs`, isLong: false, ms };

  const totalHours = Math.floor(ms / 3600000);
  const days = Math.floor(totalHours / 24);
  return { label: `${days}d ${totalHours - days * 24}hrs`, isLong: true, ms };
};

/**
 * Turnaround for a job that has already stopped - how long it TOOK, not how
 * long ago it was logged. Counting up from createdDate forever is only
 * meaningful while a job is live; on a Completed/Closed row it only reported
 * how stale the record was, which tells a reviewer nothing.
 *
 * Whole calendar days deliberately: CompletedDate/ClosedDate are SQL DATE
 * (midnight) while CreatedDate is DATETIME, so anything finer is invented
 * precision - and a same-day completion would otherwise come out negative.
 */
export const formatTurnaroundDays = (loggedIso, finishedIso) => {
  if (!loggedIso || !finishedIso) return null;
  const logged = new Date(loggedIso);
  const finished = new Date(finishedIso);
  if (Number.isNaN(logged.getTime()) || Number.isNaN(finished.getTime())) return null;

  const days = Math.round(
    (new Date(finished.getFullYear(), finished.getMonth(), finished.getDate()) -
      new Date(logged.getFullYear(), logged.getMonth(), logged.getDate())) /
      86400000
  );
  if (days < 0) return null;
  if (days === 0) return "Same day";
  return days === 1 ? "1 day" : `${days} days`;
};

/** Row-shaped wrapper over the status vocabulary's own COMPLETED/CLOSED test. */
export const isFinalized = (row) => isFinalizedStatus(row?.status);

export const finishedDateOf = (row) => row?.closedDate || row?.completedDate || null;

/**
 * What the row's own clock counts from. A job that is running has been running
 * since it STARTED - counting from when it was logged would keep punishing a
 * job for the queue time it has already left behind.
 */
export const elapsedFromOf = (row) =>
  row?.status === "ONGOING" ? row?.startedDate || row?.createdDate : row?.createdDate;

/**
 * Queue order for the work list (2026-09-09, direct instruction: "closed orders
 * are not to be shown on top... sort based on not started, assigned and time
 * elapsed").
 *
 * The list is a worklist, so it is ordered by what needs a decision: nothing has
 * happened to a NOT_STARTED job at all, an ASSIGNED one has a technician but has
 * not begun, ONGOING is in hand, ON_HOLD is parked on purpose - and the two
 * finished states are history, so they sink to the bottom rather than riding on
 * top just for being the newest rows.
 */
const STATUS_RANK = {
  NOT_STARTED: 0,
  ASSIGNED: 1,
  ONGOING: 2,
  ON_HOLD: 3,
  COMPLETED: 4,
  CLOSED: 5,
};

const rankOf = (row) => {
  const rank = STATUS_RANK[row?.status];
  // An unknown status sorts with the live work rather than vanishing to the
  // bottom - a state nobody has ranked yet is far more likely to need a look
  // than a closed job is.
  return rank === undefined ? 2.5 : rank;
};

const timeOf = (value) => {
  const parsed = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const sortWorkQueue = (rows) =>
  [...rows].sort((a, b) => {
    const byRank = rankOf(a) - rankOf(b);
    if (byRank !== 0) return byRank;

    // Live work: longest-waiting first, which is the same order as the red
    // pills reading downward.
    if (!isFinalized(a)) return timeOf(elapsedFromOf(a)) - timeOf(elapsedFromOf(b));

    // Finished work: most recently finished first. Age is not a priority on a
    // job nobody has to act on, but "what did we just close" is worth seeing.
    return timeOf(finishedDateOf(b)) - timeOf(finishedDateOf(a));
  });
